import React from "react";
import { styled, css } from "styled-components";
import { mediaQueries as media } from "../../styles";
import { Button } from "../Button";
import PopupGallery from "../PopupGallery";
import VisibilitySensor from "../VisibilitySensor";
import { TranslatableGalleryStrings, } from "./types";
const StyledGallery = styled.div `
  display: flex;
  flex-direction: row;
  margin-bottom: 1.5625rem;

  ${media.down("medium") `
    flex-direction: column;
  `}
`;
const StyledFigure = styled.figure `
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
`;
const PictureContainer = styled.div `
  overflow: hidden;
  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`;
const getImageHeight = (large, hasCaption) => {
    if (large) {
        return "460px";
    }
    if (hasCaption) {
        return "210px";
    }
    return "225px";
};
const StyledImageContainer = styled.div `
  width: 100%;
  height: ${(props) => getImageHeight(!!props.large, !!props.hasCaption)};
  ${media.down("small") `
      height: ${(props) => (props.large ? "190px" : "120px")};
    `};

  > * {
    width: 100% !important;
    display: block !important;
  }

  img {
    display: block;
    width: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
    height: ${(props) => getImageHeight(!!props.large, !!props.hasCaption)};
    ${media.down("small") `
      height: ${(props) => (props.large ? "190px" : "120px")};
    `};
  }
`;
const StyledCaption = styled.figcaption `
  font-size: 14px;
  background-color: #eeeeee;
  padding: 5px 10px;
  margin: 0;
  min-height: 27px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${media.down("small") `
    min-height: 23px;
    font-size: 13px;
  `};
`;
const StyledCaptionInner = styled.div `
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${(props) => props.large &&
    css `
      max-width: 500px;
    `}

  ${media.down("small") `
    max-width: 100px;

    ${(props) => props.large &&
    css `
        max-width: 300px;
      `}
  `}
  ${media.only("medium", "xlarge") `
    max-width: 90px;

    ${(props) => props.large &&
    css `
        max-width: 300px;
      `}
  `}
`;
const GridContainer = styled.div `
  display: flex;
  flex-wrap: wrap;
  margin: 0 5px 0 -5px;

  ${({ isColumn }) => isColumn &&
    css `
      flex-direction: column;
    `}

  ${media.down("medium") `
    margin-right: -5px;
  `};
`;
const GridContainerItem = styled.div `
  flex: 0 0 calc(50% - 10px);
  margin: 0 5px 10px;
  display: flex;
  ${media.up("medium") `
    &:nth-child(3),
    &:nth-child(4) {
      margin-bottom: 0;
      align-items: flex-end;
    }
  `}
`;
const FirstItem = styled.div `
  display: flex;

  flex: ${({ isAlone }) => (isAlone ? "1" : "0 0 620px")};

  ${media.down("large") `
    flex: 0 0 55%;
  `}
  ${media.down("small") `
    height: 209px;
  `}
`;
const GalleryItemLink = styled.button `
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  line-height: normal;
  outline: none;
  display: block;
  cursor: pointer;
  width: 100%;
  background: transparent;
`;
const Placeholder = styled.div `
  min-width: 1px;
  width: 100%;
  height: 0;
  box-sizing: content-box;
  padding-bottom: ${(props) => props.aspectRatio}%; /* Aspect ratio of the width/height */
  background: #cccccc;
`;
const DefaultGalleryItemImageRenderer = ({ image }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { caption, alt, src, ...imageProps } = image;
    return React.createElement("img", { src: src, alt: alt, ...imageProps });
};
export const GalleryItem = React.memo(({ imageIndex, image, large, isVisible, amountOfImages, handleOpenGallery, showCaption, imageRenderer = DefaultGalleryItemImageRenderer, }) => (React.createElement(GalleryItemLink, { onClick: () => handleOpenGallery(imageIndex) },
    React.createElement(StyledFigure, null,
        React.createElement(PictureContainer, null, isVisible ? (React.createElement(StyledImageContainer, { large: large || amountOfImages === 2, hasCaption: showCaption }, imageRenderer({ image, options: { large, showCaption } }))) : (React.createElement(Placeholder, { aspectRatio: 16 / 9 }))),
        image.caption && showCaption && (React.createElement(StyledCaption, null,
            React.createElement(StyledCaptionInner, { large: large }, image.caption)))))));
export const Gallery = ({ images, label, texts, popupGalleryImageRenderer, persistentInfo, galleryItemImageRenderer = DefaultGalleryItemImageRenderer, }) => {
    const [isGalleryOpen, setIsGalleryOpen] = React.useState(false);
    const [galleryIndexOpen, setGalleryIndexOpen] = React.useState(0);
    const lastImageIndex = images.length > 4 ? 4 : images.length - 1;
    const gridImages = images.slice(0, lastImageIndex);
    const largeImage = images[lastImageIndex];
    const imagesCount = images && images.length > 0 ? images.length : 0;
    const closeGallery = () => {
        setIsGalleryOpen(false);
    };
    const translations = { ...TranslatableGalleryStrings, ...texts };
    const handleOpenGallery = (index) => {
        setGalleryIndexOpen(index);
        setIsGalleryOpen(true);
    };
    return (React.createElement(VisibilitySensor, { component: (isVisible) => (React.createElement("div", null,
            React.createElement(StyledGallery, null,
                gridImages && gridImages.length > 0 && (React.createElement(GridContainer, { isColumn: gridImages.length <= 2 }, gridImages.map((image, imageIndex) => (React.createElement(GridContainerItem, { key: image.key || imageIndex },
                    React.createElement(GalleryItem, { amountOfImages: images.length, imageRenderer: galleryItemImageRenderer, isVisible: isVisible, imageIndex: imageIndex, handleOpenGallery: handleOpenGallery, image: image, showCaption: Boolean(imagesCount === 1) })))))),
                React.createElement(FirstItem, { isAlone: images.length === 1 },
                    React.createElement(GalleryItem, { amountOfImages: images.length, imageRenderer: galleryItemImageRenderer, isVisible: isVisible, imageIndex: lastImageIndex, handleOpenGallery: handleOpenGallery, image: largeImage, showCaption: Boolean(imagesCount === 1), large: true }))),
            images.length > 5 && (React.createElement(Button, { theme: "clean", onClick: () => handleOpenGallery(4) }, translations.OPEN_GALLERY_BUTTON_TEXT)),
            React.createElement(PopupGallery, { persistentInfo: persistentInfo, imageRenderer: popupGalleryImageRenderer, label: label, images: images, initialIndex: galleryIndexOpen, isOpen: isGalleryOpen, onRequestClose: closeGallery }))) }));
};
export default Gallery;
//# sourceMappingURL=Gallery.js.map